import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listUsers, adminAddMember } from "@/lib/api/admin";
import type { AdminUser } from "@/lib/api/types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";

type AddMemberDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  role: "owner" | "manager" | "staff";
};

export function AddMemberDialog({ open, onOpenChange, storeId, role }: AddMemberDialogProps) {
  const user = useAdminAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState<AdminUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [formError, setFormError] = useState("");

  async function handleSearch() {
    if (!user || !search.trim()) return;
    setIsSearching(true);
    try {
      const res = await listUsers(user, { search: search.trim(), limit: 10 });
      setResults(res.data);
    } finally {
      setIsSearching(false);
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () => adminAddMember(user!, storeId, selected!.user_uid, role),
    onSuccess: () => {
      const label = role === "owner" ? "Owner" : role === "manager" ? "Manager" : "Staff member";
      toast.success(`${label} added`);
      void queryClient.invalidateQueries({ queryKey: ["store-members", storeId] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSearch("");
      setResults([]);
      setSelected(null);
      setFormError("");
    }
    onOpenChange(value);
  }

  function handleSubmit() {
    setFormError("");
    mutate();
  }

  const title =
    role === "owner" ? "Add Owner" : role === "manager" ? "Add Manager" : "Add Staff Member";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {selected ? (
          <div className="space-y-3">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p className="font-medium">
                {selected.first_name} {selected.last_name}
              </p>
              {selected.email && <p className="text-xs text-muted-foreground">{selected.email}</p>}
              <p className="font-mono text-xs text-muted-foreground">{selected.user_uid}</p>
              <p className="pt-1 text-xs text-muted-foreground">
                Joined {new Date(selected.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              type="button"
              className="text-sm text-primary underline underline-offset-2"
              onClick={() => setSelected(null)}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Search user</Label>
              <div className="flex gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name or email"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSearch();
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleSearch()}
                  disabled={isSearching || !search.trim()}
                >
                  {isSearching ? <Spinner className="size-4 border-primary" /> : "Search"}
                </Button>
              </div>
            </div>

            {results.length > 0 && (
              <ul className="divide-y rounded-md border text-sm">
                {results.map((u) => (
                  <li
                    key={u.user_uid}
                    className="flex cursor-pointer flex-col gap-0.5 px-3 py-2 transition-colors hover:bg-muted/50"
                    onClick={() => setSelected(u)}
                  >
                    <span className="font-medium">
                      {u.first_name} {u.last_name}
                    </span>
                    {u.email && <span className="text-xs text-muted-foreground">{u.email}</span>}
                    <span className="font-mono text-xs text-muted-foreground">{u.user_uid}</span>
                    <span className="text-xs text-muted-foreground">
                      Joined {new Date(u.created_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {results.length === 0 && search && !isSearching && (
              <p className="text-sm text-muted-foreground">No users found.</p>
            )}
          </div>
        )}

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selected || isPending}>
            {isPending ? <Spinner className="size-4 border-white" /> : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
